// src/v3/ui/pinbar/pinbar.hooks.ts
//
// TanStack Query server-state layer for the pinbar feature.
// All reads use useApiQuery, all mutations use useApiMutation with key-based
// invalidation. No ad-hoc useState+useEffect+apiFetch for server-state.
// No direct apiFetch/fetch — all HTTP goes through the _common wrappers.

import { useQueryClient } from "@tanstack/react-query";
import { qk, useApiMutation, useApiQuery } from "../_common";
import type { FilterClause, SortClause } from "../_deps";

export interface PinbarItem {
  id: string;
  symbol: string;
  type: string;
  date: string;
  pinbarStatus: string;
  approveStatus: string;
  reasoningText: string | null;
  strategyVersion: number;
  context: Record<string, unknown> | null;
  comment: string | null;
  symbolStatus: string | null;
  timestamp?: { createdAt: Date; updatedAt: Date };
}

export interface PinbarListParams {
  date?: string;
  page: number;
  pageSize: number;
  filters: FilterClause[];
  sort: SortClause[];
}

export interface PinbarListResult {
  items: PinbarItem[];
  total: number;
}

export interface PinbarPrompt {
  content: string;
  version: number;
}

interface IdentifyResult {
  status: string;
  items: PinbarItem[];
}

interface VerifyAiResult {
  status: string;
  verifiedCount: number;
}

// ── Reads ───────────────────────────────────────────────────────────────────

/** Read: list pinbars with date filter + pagination/filter/sort. */
export function usePinbars(params: PinbarListParams) {
  const offset = (params.page - 1) * params.pageSize;
  const searchParams = new URLSearchParams();
  if (params.date) searchParams.set("date", params.date);
  searchParams.set("limit", String(params.pageSize));
  searchParams.set("offset", String(offset));
  if (params.filters.length > 0) {
    searchParams.set("filters", JSON.stringify(params.filters));
  }
  if (params.sort.length > 0) {
    searchParams.set(
      "sort",
      params.sort.map((s) => `${s.column}:${s.direction}`).join(","),
    );
  }
  const qs = searchParams.toString();
  return useApiQuery<PinbarListResult>(
    qk.pinbar.list(params),
    `/api/pinbars${qs ? `?${qs}` : ""}`,
  );
}

/** Read: single pinbar detail by id. */
export function usePinbarDetail(id: string | null | undefined) {
  return useApiQuery<PinbarItem>(
    qk.pinbar.detail(id ?? ""),
    `/api/pinbars/${id}`,
    { enabled: Boolean(id) },
  );
}

/** Read: active AI analysis prompt. */
export function usePinbarPrompt(enabled: boolean) {
  return useApiQuery<PinbarPrompt>(qk.pinbar.prompt, "/api/pinbars/prompt", {
    enabled,
  });
}

// ── Mutations ───────────────────────────────────────────────────────────────

/** Mutation: identify pinbars for a date. Invalidates pinbar.list. */
export function useIdentifyPinbars() {
  const queryClient = useQueryClient();
  return useApiMutation<
    { date: string; skipAi: boolean; candleMode: string },
    IdentifyResult
  >(
    (b) =>
      `/api/pinbars/identify?barDate=${encodeURIComponent(b.date)}&skipAi=${b.skipAi}&candleMode=${encodeURIComponent(b.candleMode)}`,
    "POST",
    {
      timeoutMs: 300_000,
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: qk.pinbar.list() });
      },
    },
  );
}

/** Mutation: AI-verify pinbars by date. Invalidates pinbar.list. */
export function useVerifyPinbarsAiByDate() {
  const queryClient = useQueryClient();
  return useApiMutation<{ date: string; candleMode: string }, VerifyAiResult>(
    (b) =>
      `/api/pinbars/verify-ai?date=${encodeURIComponent(b.date)}&candleMode=${encodeURIComponent(b.candleMode)}`,
    "POST",
    {
      timeoutMs: 300_000,
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: qk.pinbar.list() });
      },
    },
  );
}

/** Mutation: AI-verify a single pinbar by id. Invalidates pinbar.list + detail. */
export function useVerifyPinbarsAiById() {
  const queryClient = useQueryClient();
  return useApiMutation<
    { pinbarId: string; candleMode: string },
    VerifyAiResult
  >(
    (b) =>
      `/api/pinbars/verify-ai?pinbarId=${encodeURIComponent(b.pinbarId)}&candleMode=${encodeURIComponent(b.candleMode)}`,
    "POST",
    {
      timeoutMs: 300_000,
      onSuccess: (_data, variables) => {
        void queryClient.invalidateQueries({ queryKey: qk.pinbar.list() });
        void queryClient.invalidateQueries({
          queryKey: qk.pinbar.detail(variables.pinbarId),
        });
      },
    },
  );
}

/** Mutation: update a pinbar by id (partial body). Invalidates list + detail. */
export function useUpdatePinbar() {
  const queryClient = useQueryClient();
  return useApiMutation<
    { id: string; fields: Partial<PinbarItem> },
    PinbarItem
  >(
    (b) => `/api/pinbars/${encodeURIComponent(b.id)}`,
    "PUT",
    {
      body: (b) => b.fields,
      onSuccess: (_data, variables) => {
        void queryClient.invalidateQueries({ queryKey: qk.pinbar.list() });
        void queryClient.invalidateQueries({
          queryKey: qk.pinbar.detail(variables.id),
        });
      },
    },
  );
}

/** Mutation: reset AI verification for a pinbar. Invalidates list + detail. */
export function useResetAiVerification() {
  const queryClient = useQueryClient();
  return useApiMutation<{ id: string }, unknown>(
    (b) => `/api/pinbars/reset-ai?id=${encodeURIComponent(b.id)}`,
    "POST",
    {
      onSuccess: (_data, variables) => {
        void queryClient.invalidateQueries({ queryKey: qk.pinbar.list() });
        void queryClient.invalidateQueries({
          queryKey: qk.pinbar.detail(variables.id),
        });
      },
    },
  );
}

/** Mutation: update the AI analysis prompt. Invalidates pinbar.prompt. */
export function useUpdatePinbarPrompt() {
  const queryClient = useQueryClient();
  return useApiMutation<{ content: string }, PinbarPrompt>(
    "/api/pinbars/prompt",
    "PUT",
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: qk.pinbar.prompt });
      },
    },
  );
}

/** Mutation: export pinbars for a date (downloads JSON blob). */
export function useExportPinbars() {
  return useApiMutation<{ date: string }, unknown[]>(
    "/api/pinbars/export",
    "POST",
  );
}
